<template>
  <div class="px-2 py-4 sm:px-4 sm:py-6">
    <!-- Tabs - sticky header -->
    <PageTabs v-model="activeTab" :tabs="tabs" />

    <!-- Content container with minimum height to prevent layout shifts -->
    <div class="min-h-[400px] mt-6">
      <!-- Loading -->
      <div v-if="isLoading" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p class="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
      </div>

      <!-- Urgent (Spoed) Tab -->
      <BeoordelenUrgentTab
        v-else-if="activeTab === 'urgent'"
        :urgent-prospects="urgentProspects"
        :get-team-card-class="getTeamCardClass"
        :format-date-time="formatDateTime"
        @open-review="openReviewModal"
      />

      <!-- My Pending Tab -->
      <BeoordelenMyPendingTab
        v-else-if="activeTab === 'my-pending'"
        :my-pending-prospects="myPendingProspects"
        :format-date-time="formatDateTime"
        @open-review="openReviewModal"
      />

      <!-- All Pending Tab -->
      <BeoordelenAllPendingTab
        v-else-if="activeTab === 'all-pending'"
        :pending-prospects="pendingProspects"
        :get-team-card-class="getTeamCardClass"
        :format-date-time="formatDateTime"
        @open-review="openReviewModal"
      />

      <!-- Recent Tab -->
      <BeoordelenRecentTab
        v-else-if="activeTab === 'recent'"
        :recent-prospects="recentProspects"
        :get-status-label="getStatusLabel"
        :get-status-badge-class="getStatusBadgeClass"
        :format-date-time="formatDateTime"
      />
    </div>

    <!-- Review Modal -->
    <BeoordelenReviewModal
      :selected-prospect="selectedProspect"
      :teams="teams"
      :on-update-team="handleUpdateTeam"
      :selected-preferences="selectedPreferences"
      :loading-preferences="loadingPreferences"
      :pl-selections="plSelections"
      :local-proposals="localProposals"
      :comparison-employees="comparisonEmployees"
      :has-active-concept="hasActiveConcept"
      :concept-loaded="conceptLoaded"
      :concept-saved-at="conceptSavedAt"
      :has-unsaved-changes="hasUnsavedChanges"
      :employee-map="employeeMap"
      :employee-details="employeeDetails"
      :employee-availability="employeeAvailability"
      :expanded-preferences="expandedPreferences"
      :availability-expanded="availabilityExpanded"
      :employee-graph-expanded="employeeGraphExpanded"
      :newly-added-pref-id="newlyAddedPrefId"
      :all-roles-have-selection="allRolesHaveSelection"
      :is-adding-role="isAddingRole"
      :add-role-error="addRoleError"
      :review-feedback="reviewFeedback"
      :is-processing="isProcessing"
      :format-date-time="formatDateTime"
      @close="closeModal"
      @prospect-updated="handleProspectUpdated"
      @select-employee="selectEmployee"
      @select-comparison="selectComparisonEmployee"
      @add-employee="openEmployeeModal"
      @remove-comparison="removeComparisonEmployee"
      @toggle-expanded="togglePreferenceExpanded"
      @toggle-availability="toggleAvailabilityExpanded"
      @toggle-employee-graph="toggleEmployeeGraph"
      @preference-updated="handlePreferenceUpdated"
      @delete-preference="handleDeletePreference"
      @add-role="handleAddRole"
      @update:review-feedback="reviewFeedback = $event"
      @approve="approveProspect"
      @reject="rejectProspect"
      @discuss="discussProspect"
      @heraanvraag="heraanvraagProspect"
      @save-concept="saveConceptAsDraft"
      @discard-concept="discardConcept"
    />

    <!-- Employee Selection Modal -->
    <AssignEmployeeModal
      :open="showEmployeeModal"
      :project="selectedProspect"
      :assigned-employee-ids="getAssignedEmployeeIds()"
      :initial-team-id="currentPreferenceTeam"
      :initial-function="currentPreferenceFunction"
      :initial-dpw="currentPreferenceDpw"
      :period-start="currentPreferencePeriodStart"
      :period-end="currentPreferencePeriodEnd"
      :local-proposals="localProposals"
      :current-preference-id="currentPreferenceId"
      :preferences="selectedPreferences"
      :default-availability-mode="'voorgesteld'"
      @close="showEmployeeModal = false"
      @select="handleEmployeeSelected"
    />
  </div>
</template>

<script setup>
import { useBeoordelen } from '@/composables/domain/useBeoordelen.js'
import AssignEmployeeModal from '@/components/common/modals/AssignEmployeeModal.vue'
import PageTabs from '@/components/ui/PageTabs.vue'
import BeoordelenUrgentTab from '@/components/beoordelen/BeoordelenUrgentTab.vue'
import BeoordelenMyPendingTab from '@/components/beoordelen/BeoordelenMyPendingTab.vue'
import BeoordelenAllPendingTab from '@/components/beoordelen/BeoordelenAllPendingTab.vue'
import BeoordelenRecentTab from '@/components/beoordelen/BeoordelenRecentTab.vue'
import BeoordelenReviewModal from '@/components/beoordelen/BeoordelenReviewModal.vue'

const {
  // Tab & loading
  activeTab,
  isLoading,
  tabs,
  urgentProspects,
  pendingProspects,
  recentProspects,
  myPendingProspects,

  // Modal state
  selectedProspect,
  selectedPreferences,
  loadingPreferences,
  reviewFeedback,
  isProcessing,
  expandedPreferences,

  // Add role
  isAddingRole,
  addRoleError,
  newlyAddedPrefId,

  // Employee data
  employeeMap,
  employeeDetails,
  employeeAvailability,

  // Teams
  teams,

  // PL selections
  plSelections,
  allRolesHaveSelection,
  localProposals,

  // Concept (local draft)
  hasActiveConcept,
  conceptLoaded,
  conceptSavedAt,
  hasUnsavedChanges,
  saveConceptAsDraft,
  discardConcept,

  // Availability
  availabilityExpanded,

  // Employee modal
  showEmployeeModal,
  currentPreferenceId,
  currentPreferenceTeam,
  currentPreferenceFunction,
  currentPreferenceDpw,
  currentPreferencePeriodStart,
  currentPreferencePeriodEnd,

  // Comparison employees
  comparisonEmployees,
  employeeGraphExpanded,

  // Team helpers
  getTeamCardClass,

  // Toggle helpers
  togglePreferenceExpanded,
  toggleAvailabilityExpanded,
  toggleEmployeeGraph,

  // Modal
  openReviewModal,
  closeModal,

  // Employee selection
  selectEmployee,
  selectComparisonEmployee,
  openEmployeeModal,
  getAssignedEmployeeIds,
  handleEmployeeSelected,
  removeComparisonEmployee,

  // Team update
  handleUpdateTeam,
  handleProspectUpdated,

  // Add role
  handleAddRole,
  handlePreferenceUpdated,
  handleDeletePreference,

  // Workflow actions
  approveProspect,
  rejectProspect,
  discussProspect,
  heraanvraagProspect,

  // Status helpers
  getStatusLabel,
  getStatusBadgeClass,

  // Formatting
  formatDateTime
} = useBeoordelen()
</script>
